/*
  Hekaya motion layer — watches the page and adds animation classes.
  It never changes content or store logic; if it fails, the page simply stays static.
*/
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const REVEAL_SELECTOR = [
    '.fudge-section-header',
    '.fudge-category-card',
    '.fudge-pcard',
    '.fudge-trust-col',
    '.fudge-pdp-trust-panel',
    '.fudge-pdp-payments-box',
    '.fudge-pdp-accordions-section',
    '.fudge-pdp-recs-header',
    '.fudge-cat-loadmore-wrap',
    '.fudge-branch-pills',
    '.fudge-carters-link'
  ].join(',');

  const STAGGER_MS = 70;
  const STAGGER_STEPS = 5;

  document.documentElement.classList.add('hk-motion');

  /** @type {Set<Element>} elements still waiting to be revealed */
  const pending = new Set();

  /** @param {Element} el */
  function reveal(el) {
    el.classList.add('hk-in');
    pending.delete(el);
    revealObserver.unobserve(el);
  }

  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target);
      }
    },
    { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
  );

  // Safety net: never leave something hidden on screen if the observer is late
  function revealOnScreen() {
    const limit = window.innerHeight;
    for (const el of pending) {
      if (!el.isConnected) {
        pending.delete(el);
        revealObserver.unobserve(el);
        continue;
      }
      const rect = el.getBoundingClientRect();
      if (rect.top < limit && rect.bottom > 0) reveal(el);
    }
  }

  let safetyTimer = 0;
  function scheduleSafetyCheck() {
    clearTimeout(safetyTimer);
    safetyTimer = window.setTimeout(revealOnScreen, 250);
  }

  window.addEventListener('scroll', scheduleSafetyCheck, { passive: true });
  window.addEventListener('resize', scheduleSafetyCheck, { passive: true });
  window.addEventListener('load', scheduleSafetyCheck);

  /** @param {Element} el */
  function prepare(el) {
    if (el.classList.contains('hk-reveal')) return;
    // Cards appended by "عرض المزيد" already have their own fade-in
    if (el.classList.contains('fudge-pcard--appended')) return;
    const siblings = el.parentElement ? [...el.parentElement.children] : [el];
    const position = siblings.indexOf(el) % STAGGER_STEPS;
    el.style.setProperty('--hk-delay', `${position * STAGGER_MS}ms`);
    el.classList.add('hk-reveal');
    pending.add(el);
    revealObserver.observe(el);
    scheduleSafetyCheck();
  }

  /** @param {ParentNode} root */
  function scan(root) {
    if (root instanceof Element && root.matches(REVEAL_SELECTOR)) prepare(root);
    for (const el of root.querySelectorAll(REVEAL_SELECTOR)) prepare(el);
  }

  scan(document);

  // Cards rendered later by the theme scripts (tabs, live collections, search)
  const domObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) scan(node);
      }
    }
  });
  domObserver.observe(document.body, { childList: true, subtree: true });

  /** Restart a one-shot animation class. @param {Element | null} el @param {string} cls */
  function replay(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void /** @type {HTMLElement} */ (el).offsetWidth;
    el.classList.add(cls);
    el.addEventListener('animationend', () => el.classList.remove(cls), { once: true });
  }

  // Cart icon bump when the badge number goes up
  const badge = document.getElementById('fudge-header-cart-badge');
  const cartBtn = document.getElementById('fudge-header-cart-btn');
  if (badge) {
    let lastCount = parseInt(badge.textContent || '0', 10) || 0;
    new MutationObserver(() => {
      const count = parseInt(badge.textContent || '0', 10) || 0;
      if (count > lastCount) {
        replay(cartBtn, 'hk-bump');
        replay(badge, 'hk-bump');
      }
      lastCount = count;
    }).observe(badge, { childList: true, characterData: true, subtree: true });
  }

  // Product page: smooth image swap
  const mainImg = document.getElementById('fudge-pdp-main-image');
  if (mainImg) {
    new MutationObserver(() => replay(mainImg, 'hk-swap')).observe(mainImg, {
      attributes: true,
      attributeFilter: ['src']
    });
  }

  // Product page: check mark when "تمت الإضافة" appears on the button
  const submitBtn = document.getElementById('fudge-pdp-submit-btn');
  if (submitBtn) {
    new MutationObserver(() => {
      const added = (submitBtn.textContent || '').includes('تمت الإضافة');
      submitBtn.classList.toggle('hk-success', added);
    }).observe(submitBtn, { childList: true, characterData: true, subtree: true });
  }
})();
